// TVTool B7a (spec §9.3, §9.4) — the training-video cache as seen from the app.
// The service worker owns the cache `agxl-training-v1` (public/sw.js: PRECACHE_TRAINING / TRIM_TRAINING messages,
// range-aware cache-first serving of every /onboarding-videos/training/ URL, never evicted by the shared 50 MB
// cache, kept across app updates). This module asks for persistence once, reads free space, lists what is cached
// (the Cache API is readable from the page) and remembers which videos the farmer has played ("New" mark).
// Every browser API here can be absent (no service worker, private mode, old WebView): each helper degrades to
// "nothing cached / unknown space" and never throws into the screen.
import { TRAINING_PATH, isTrainingUrl } from './trainingVideos';

export const TRAINING_CACHE = 'agxl-training-v1';
const PERSIST_KEY = 'agxl_training_persist_asked';
const PLAYED_KEY = 'agxl_training_played';

/** §9.3.2: ask the browser once to keep our storage (never evicted under pressure). */
export async function persistOnce(): Promise<boolean | null> {
  try {
    if (localStorage.getItem(PERSIST_KEY) === '1') return (await navigator.storage?.persisted?.()) ?? null;
    if (!navigator.storage?.persist) return null;
    const granted = (await navigator.storage.persisted?.()) === true || (await navigator.storage.persist()) === true;
    localStorage.setItem(PERSIST_KEY, '1');
    return granted;
  } catch {
    return null;
  }
}

/** Bytes still free in our origin's quota, or null where estimate() is unsupported. */
export async function freeBytes(): Promise<number | null> {
  try {
    if (!navigator.storage?.estimate) return null;
    const e = await navigator.storage.estimate();
    if (typeof e.quota !== 'number' || typeof e.usage !== 'number') return null;
    return Math.max(0, e.quota - e.usage);
  } catch {
    return null;
  }
}

/** The training URLs the cache holds right now. */
export async function cachedTrainingUrls(): Promise<Set<string>> {
  try {
    if (typeof caches === 'undefined') return new Set();
    const cache = await caches.open(TRAINING_CACHE);
    const keys = await cache.keys();
    return new Set(keys.map((r) => r.url).filter(isTrainingUrl));
  } catch {
    return new Set();
  }
}

function swController(): ServiceWorker | null {
  try {
    return navigator.serviceWorker?.controller ?? null;
  } catch {
    return null;
  }
}

/** §9.3.3 background download: the service worker fetches and stores each URL (full object, no Range). Without a
 *  controlling worker (first visit) the page stores them itself — same cache, same keys. */
export async function precacheTraining(items: { url: string; bytes: number | null }[]): Promise<void> {
  const list = items.filter((i) => isTrainingUrl(i.url));
  if (list.length === 0) return;
  const sw = swController();
  if (sw) {
    sw.postMessage({ type: 'PRECACHE_TRAINING', data: { items: list } });
    return;
  }
  try {
    if (typeof caches === 'undefined') return;
    const cache = await caches.open(TRAINING_CACHE);
    for (const i of list) {
      try {
        if (await cache.match(i.url)) continue;
        const res = await fetch(i.url, { method: 'GET', mode: 'cors', credentials: 'omit' });
        if (res.ok && res.status === 200) await cache.put(new Request(i.url, { method: 'GET' }), res);
      } catch {
        /* one missing file never stops the pack */
      }
    }
  } catch {
    /* no Cache API */
  }
}

/** §9.4: drop cached copies no published video uses any more (older versions). */
export async function trimTraining(keepUrls: string[]): Promise<void> {
  const sw = swController();
  if (sw) {
    sw.postMessage({ type: 'TRIM_TRAINING', data: { keep: keepUrls } });
    return;
  }
  try {
    if (typeof caches === 'undefined') return;
    const cache = await caches.open(TRAINING_CACHE);
    const keep = new Set(keepUrls);
    for (const req of await cache.keys()) {
      if (!keep.has(req.url)) await cache.delete(req);
    }
  } catch {
    /* no Cache API */
  }
}

/** The media URLs the farmer has played (a newer publish has a new URL → "New" again). */
export function playedUrls(): Set<string> {
  try {
    const raw = localStorage.getItem(PLAYED_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function markPlayed(url: string): void {
  try {
    const s = playedUrls();
    s.add(url);
    localStorage.setItem(PLAYED_KEY, JSON.stringify(Array.from(s).slice(-200)));
  } catch {
    /* storage blocked — the mark is cosmetic */
  }
}

export { TRAINING_PATH };
